import {
  MdMoreHoriz,
  MdOutlineArchive,
  MdOutlineUnarchive,
  MdOutlineSwapVert,
  MdOutlineAutoDelete,
  MdRestoreFromTrash,
  MdOutlineCreditCard,
  MdOutlineContentCopy,
  MdOutlineFileDownload,
  MdDelete,
  MdConnectWithoutContact,
  MdFilterAlt,
  MdFilterAltOff,
  MdAdd,
} from "react-icons/md";
import { v4 as uuidv4 } from "uuid";
import { get, map, orderBy } from "lodash-es";

import PageTitle from "../../components/PageTitle/PageTitle";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import { useEffect, useMemo, useState, useTransition } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import clsx from "clsx";
import userPreferences from "../../store/userPreferences";
import {
  showNotification,
  extractTransactionIds,
  transformToSelectOptions,
  exportToExcel,
  exportToCsv,
  exportToJson,
} from "../../helpers/utils";
import LinksList from "../../components/LinksList/LinksList";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import {
  BUTTON_STYLES,
  ICON_SIZES,
  TRANSACTIONS_TABS,
} from "../../helpers/constants";
import { useTranslation } from "react-i18next";
import useMobile from "../../hooks/useMobile";
import { AnimatePresence, motion } from "framer-motion";

import TransactionsTable from "./TransactionsTable";
import moment from "moment";
import TodayTotalsBar from "../../components/TodayTotalsBar/TodayTotalsBar";
import useMainFilter from "../../hooks/useMainFilter";
import {
  updateTransactionsText,
  deleteTransactions,
  cloneTransaction,
  shareTransactions,
} from "../../api/transactions";
import useAccountsDropdownMenu from "../../hooks/useAccountsDropdownMenu";
import useCurrentAccount from "../../hooks/useCurrentAccount";
import ReportsSidebar from "./ReportsSidebar";
import useFilteredTransactions from "../../hooks/useFilteredTransactions";
import CurrentAccountCard from "../../components/CurrentAccountCard/CurrentAccountCard";
import AccountNotFound from "../../components/AccountNotFound/AccountNotFound";
import PageSubtitleAccount from "../../components/PageTitle/PageSubtitleAccount";
import useGeneralOptionsDropdownMenu from "../../hooks/useGeneralOptionsDropdownMenu";
import useCache from "../../hooks/useCache";
import SelectDialog from "../../components/SelectDialog/SelectDialog";
import useCurrentUser from "../../hooks/useCurrentUser";
import ActiveMainFilterMessage from "../../components/ActiveMainFilterMessage/ActiveMainFilterMessage";
import { ICustomSelectOption } from "../../components/Select/CustomSelect";
import DuplicateTransactionsDialog from "../../components/DuplicateTransactionsDialog/DuplicateTransactionsDialog";
import SelectionTotals from "../../components/SelectionTotals/SelectionTotals";
import useCollapsibles from "../../hooks/useCollapsibles";
import ReportsYearSelector from "../../components/ReportsYearSelector/ReportsYearSelector";
import IncomeExpensesPerYear from "../../components/IncomeExpensesPerYear/IncomeExpensesPerYear";
import useModalsStore from "src/store/modalsStore";
import ScrollToSection from "src/components/ScrollToSection/ScrollToSection";
import ExportTransactionsModal, {
  EXPORT_TYPE,
  FILE_TYPES,
} from "src/components/ExportTransactionsModal/ExportTransactionsModal";
import SlideDownUpAnimation from "src/components/Animations/SlideDownUpAnimation";
import useDarkMode from "src/hooks/useDarkMode";
import ShareTransactionModal from "src/components/ShareTransactionModal/ShareTransactionModal";
import InfoBlock from "src/components/InfoBlock/InfoBlock";
import { EButtonKind } from "src/components/Button/ButtonTypes";
import MarqueeRow from "src/components/MarqueeRow/MarqueeRow";
import LoadingWithTextPage from "src/components/LoadingWithText/LoadingWithTextPage";
import { ETransactionsTableKeys } from "src/components/Tables/tableIds";

const Transactions: React.FC = () => {
  const [isPending, startTransition] = useTransition();
  const navigate = useNavigate();

  const accountsGlobalMenu = useAccountsDropdownMenu({
    linkTo: "transactions",
  });
  const { isEnabled: isDarkMode } = useDarkMode();
  const [canScroll, setCanScroll] = useState(false);
  const [isExportingData, setIsExportingData] = useState<boolean>(false);

  const { collapsiblePreferences } = useCollapsibles();
  const {
    archivedTransactions,
    isLoadingCurrentAccount,
    currentAccountData,
    deletedTransactions,
    currencySymbol,
    currencyCode,
  } = useCurrentAccount();
  const { allUserAccounts } = useCurrentUser();

  const {
    isFilteringTransactions,
    filteredTransactions,
    transactionsFilteredByYear,
  } = useFilteredTransactions();
  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();

  const { transactionsActiveTab, setTransactionsActiveTab } = userPreferences();
  const [activeTab, setActiveTab] = useState<number>(transactionsActiveTab);
  const { isMobile } = useMobile();
  const { setShowSearchModal } = useModalsStore();
  const { setMainFilter, mainFilter, isMainFilterActive } = useMainFilter();
  const { clearCache } = useCache();
  const [showDuplicateTransactionDialog, setShowDuplicateTransactionDialog] =
    useState<boolean>(false);

  const { account: accountParameter } = useParams();
  const { t } = useTranslation();

  useEffect(() => {
    startTransition(() => setTransactionsActiveTab(activeTab));
    startTransition(() => setMainFilter({ account: accountParameter }));
  }, [activeTab, accountParameter]);

  const isLoadingSomething = useMemo(
    () =>
      [!!isLoadingCurrentAccount, !!isFilteringTransactions]?.includes(true),
    [isLoadingCurrentAccount, isFilteringTransactions]
  );

  const [selectedRows, setSelectedRows] = useState<Array<any>>([]);

  const [showExportDialog, setShowExportDialog] = useState<boolean>(false);
  const [isMovingTransactions, setIsMovingTransactions] =
    useState<boolean>(false);
  const [isDuplicatingTransactions, setIsDuplicatingTransactions] =
    useState<boolean>(false);
  const [isDeletingTransactions, setIsDeletingTransactions] =
    useState<boolean>(false);
  const [isSharingTransactions, setIsSharingTransactions] =
    useState<boolean>(false);
  const [showShareTransactionsDialog, setShowShareTransactionsDialog] =
    useState<boolean>(false);
  const [isArchivingTransactions, setIsArchivingTransactions] =
    useState<boolean>(false);
  const [showArchiveTransactionsDialog, setShowArchiveTransactionsDialog] =
    useState<boolean>(false);
  const [isRestoringTransactions, setIsRestoringTransactions] =
    useState<boolean>(false);

  const [showRestoreTransactionsDialog, setShowRestoreTransactionsDialog] =
    useState<boolean>(false);

  const [showDeleteTransactionsDialog, setShowDeleteTransactionsDialog] =
    useState<boolean>(false);

  const [isRecyclingTransactions, setIsRecyclingTransactions] =
    useState<boolean>(false);
  const [showRecycleTransactionsDialog, setShowRecycleTransactionsDialog] =
    useState<boolean>(false);

  const [showMoveTransactionsDialog, setShowMoveTransactionsDialog] =
    useState<boolean>(false);
  const [showUnarchiveTransactionsDialog, setShowUnarchiveTransactionsDialog] =
    useState<boolean>(false);
  const [isUnarchivingTransactions, setIsUnarchivingTransactions] =
    useState<boolean>(false);

  useEffect(() => {
    if (!selectedRows?.length) {
      setShowDeleteTransactionsDialog(false);
      setShowMoveTransactionsDialog(false);
      setShowShareTransactionsDialog(false);
      setShowArchiveTransactionsDialog(false);
      setShowUnarchiveTransactionsDialog(false);
      setShowRestoreTransactionsDialog(false);
      setShowRecycleTransactionsDialog(false);
    }
  }, [selectedRows]);

  useEffect(() => {
    setTransactionsActiveTab(activeTab);
    setSelectedRows([]);
  }, [activeTab, setTransactionsActiveTab]);

  useEffect(() => {
    setSelectedRows([]);
  }, [accountParameter]);

  const updateSelection = async ({
    fieldsToUpdate,
  }: {
    fieldsToUpdate: object;
  }) => {
    const transactionIds = extractTransactionIds({
      transactions: selectedRows,
    });

    return await updateTransactionsText({
      transactionIds,
      fieldsToUpdate,
    });
  };

  const archiveSelection = async () => {
    setIsArchivingTransactions(true);
    let hasError = false;

    await updateSelection({
      fieldsToUpdate: { archived: true, deleted: false },
    }).then(async (data) => {
      const multipleTransactions = selectedRows?.length !== 1;

      if (hasError) {
        showNotification({
          message: `Ocurrió un error archivando ${
            multipleTransactions ? "las transacciones" : "la transacción"
          }!`,
          kind: "error",
        });
        setIsArchivingTransactions(false);
      } else {
        await clearCache();
        showNotification({
          kind: "success",
          message: multipleTransactions
            ? "Transacciones archivadas correctamente"
            : "Transacción archivada correctamente",
        });
        setSelectedRows([]);
      }

      setIsArchivingTransactions(false);
      setShowArchiveTransactionsDialog(false);
    });
  };

  const unarchiveSelection = async () => {
    setIsUnarchivingTransactions(true);
    let hasError = false;

    await updateSelection({ fieldsToUpdate: { archived: false } }).then(
      async (data) => {
        const multipleTransactions = selectedRows?.length !== 1;

        if (hasError) {
          showNotification({
            message: `Ocurrió un error desarchivando ${
              multipleTransactions ? "las transacciones" : "la transacción"
            }!`,
            kind: "error",
          });
          setIsUnarchivingTransactions(false);
        } else {
          await clearCache();
          showNotification({
            kind: "success",
            message: multipleTransactions
              ? "Transacciones desarchivadas correctamente"
              : "Transacción desarchivada correctamente",
          });

          setSelectedRows([]);
        }

        setIsUnarchivingTransactions(false);
        setShowUnarchiveTransactionsDialog(false);
      }
    );
  };

  const recycleSelection = async () => {
    let hasError = false;
    const multipleTransactions = selectedRows?.length !== 1;

    setIsRecyclingTransactions(true);

    await updateSelection({ fieldsToUpdate: { deleted: true } }).then(
      async (data) => {
        if (hasError) {
          showNotification({
            kind: "error",
            message: `Error enviando ${
              multipleTransactions ? "las transacciones" : "la transacción"
            } a la papelera!`,
          });
          setIsRecyclingTransactions(false);
        } else {
          await clearCache();

          showNotification({
            message: multipleTransactions
              ? "Transacciones enviadas a la papelera"
              : "Transacción enviada a la papelera",
            kind: "success",
          });
          setSelectedRows([]);
        }

        setIsRecyclingTransactions(false);
        setShowRecycleTransactionsDialog(false);
      }
    );
  };

  const restoreSelection = async () => {
    let hasError = false;
    const multipleTransactions = selectedRows?.length !== 1;

    setIsRestoringTransactions(true);

    await updateSelection({ fieldsToUpdate: { deleted: false } }).then(
      async (data) => {
        if (hasError) {
          showNotification({
            kind: "error",
            message: `Error restaurando ${
              multipleTransactions ? "las transacciones" : "la transacción"
            }!`,
          });
          setIsRestoringTransactions(false);
        } else {
          await clearCache();

          showNotification({
            message: multipleTransactions
              ? "Transacciones restauradas"
              : "Transacción restaurada",
            kind: "success",
          });
          setSelectedRows([]);
        }

        setIsRestoringTransactions(false);
        setShowRestoreTransactionsDialog(false);
      }
    );
  };

  const deleteSelection = async () => {
    let hasError = false;
    const multipleTransactions = selectedRows?.length !== 1;

    setIsDeletingTransactions(true);

    await deleteTransactions({
      transactionIds: extractTransactionIds({
        transactions: selectedRows,
      }),
      sendToRecycle: false,
    }).then(async (data) => {
      if (hasError) {
        showNotification({
          kind: "error",
          message: `Error eliminando ${
            multipleTransactions ? "las transacciones" : "la transacción"
          }!`,
        });
        setIsDeletingTransactions(false);
      } else {
        await clearCache();

        showNotification({
          message: multipleTransactions
            ? "Transacciones eliminadas permanentemente"
            : "Transacción eliminada permanentemente",
          kind: "success",
        });
        setSelectedRows([]);
      }

      setIsDeletingTransactions(false);
      setShowDeleteTransactionsDialog(false);
    });
  };

  const moveSelection = async ({ newAccount }: { newAccount: string }) => {
    let hasError = false;
    const multipleTransactions = selectedRows?.length !== 1;

    setIsMovingTransactions(true);

    await updateSelection({ fieldsToUpdate: { account: newAccount } }).then(
      async (data) => {
        if (hasError) {
          showNotification({
            kind: "error",
            message: `Error moviendo ${
              multipleTransactions ? "las transacciones" : "la transacción"
            }!`,
          });
          setIsMovingTransactions(false);
        } else {
          await clearCache();

          showNotification({
            message: multipleTransactions
              ? "Transacciones movidas"
              : "Transacción movida",
            kind: "success",
          });
          setSelectedRows([]);
        }

        setIsMovingTransactions(false);
        setShowMoveTransactionsDialog(false);
      }
    );
  };

  const duplicateSelection = async (customFields: any) => {
    setIsDuplicatingTransactions(true);

    const transactionIds = extractTransactionIds({
      transactions: selectedRows,
    });

    const customizedFields = {
      ...(customFields?.date?.change
        ? {
            date: moment(customFields?.date?.value).format("yyyy-MM-DD"),
          }
        : {}),
      ...(customFields?.account?.change
        ? {
            account: customFields?.account?.value,
          }
        : {}),
      recurring: {
        settings: {},
        enabled: false,
        nextPayment: "",
      },
    };

    if (transactionIds?.length) {
      const results = await Promise.allSettled(
        transactionIds?.map?.(async (transactionId: string) => {
          return await cloneTransaction({
            transactionId,
            customizeFields: customizedFields,
            newCloneId: uuidv4(),
            includeAttachments: !!customFields?.includeAttachments,
          });
        })
      );

      const successfulResults = results.filter((r) => r.status === "fulfilled");

      if (successfulResults?.length === transactionIds?.length) {
        await clearCache();
        setIsDuplicatingTransactions(false);
        setShowDuplicateTransactionDialog(false);

        showNotification({
          message:
            selectedRows?.length === 1
              ? "Transacción duplicada correctamente"
              : "Transacciones duplicadas correctamente",
          kind: "success",
        });
        setSelectedRows([]);
      } else {
        showNotification({
          message: "Ocurrió un al duplicar las transacciones",
          kind: "error",
        });
      }
    }
  };

  const attachmentsCount = useMemo(() => {
    return transactionsFilteredByYear?.reduce?.((acc: any, curr: any) => {
      const count = curr?.attachments?.length;
      if (count) {
        return acc + count;
      }
      return acc;
    }, 0);
  }, [transactionsFilteredByYear]);

  const getFileName = () => {
    return `Expenser.net - ${
      accountParameter ? `${currentAccountData?.name} ` : "All "
    }Transactions ${moment().format("yyyy-MM-DD")} ${moment().format(
      "HH_mm_ss"
    )}`;
  };

  const canExport = useMemo(() => {
    if (activeTab === TRANSACTIONS_TABS.ARCHIVED_TRANSACTIONS) {
      return !!archivedTransactions?.length;
    } else if (activeTab === TRANSACTIONS_TABS.ACTIVE_TRANSACTIONS) {
      return !!transactionsFilteredByYear?.length;
    } else if (activeTab === TRANSACTIONS_TABS.RECYCLED_TRANSACTIONS) {
      return !!deletedTransactions?.length;
    }

    return false;
  }, [
    activeTab,
    archivedTransactions,
    transactionsFilteredByYear,
    deletedTransactions,
  ]);

  const menuOptions = useMemo(() => {
    return orderBy(
      [
        {
          text: t("common_menu_items.archive"),
          onClick: () => {
            setShowArchiveTransactionsDialog(true);
          },
          icon: <MdOutlineArchive size={ICON_SIZES.MD} />,
          disabled: !selectedRows?.length,
          hide: [
            TRANSACTIONS_TABS.ARCHIVED_TRANSACTIONS,
            TRANSACTIONS_TABS.TRANSACTIONS_ATTACHMENTS,
          ].includes(activeTab),
        },
        {
          text: t("common_menu_items.unarchive"),
          onClick: () => {
            setShowUnarchiveTransactionsDialog(true);
          },
          icon: <MdOutlineUnarchive size={ICON_SIZES.MD} />,
          disabled: !selectedRows?.length,
          hide: ![TRANSACTIONS_TABS.ARCHIVED_TRANSACTIONS].includes(activeTab),
        },
        {
          icon: <MdRestoreFromTrash size={ICON_SIZES.MD} />,
          onClick: () => setShowRestoreTransactionsDialog(true),
          text: t("common_menu_items.restore"),
          kind: EButtonKind.DANGER,
          disabled: !selectedRows?.length,
          hide: ![TRANSACTIONS_TABS.RECYCLED_TRANSACTIONS].includes(activeTab),
        },
        {
          text: t("common_menu_items.duplicate"),
          onClick: () => {
            setShowDuplicateTransactionDialog(true);
          },
          icon: <MdOutlineContentCopy size={ICON_SIZES.MD} />,
          disabled: !selectedRows?.length,
          hide: [TRANSACTIONS_TABS.TRANSACTIONS_ATTACHMENTS].includes(
            activeTab
          ),
        },
        {
          text: t("common_menu_items.export"),
          onClick: () => {
            setShowExportDialog(true);
          },
          icon: <MdOutlineFileDownload size={ICON_SIZES.MD} />,
          disabled: !canExport,
        },
        {
          text: t("common_menu_items.move_account"),
          onClick: () => {
            setShowMoveTransactionsDialog(true);
          },
          icon: <MdOutlineCreditCard size={ICON_SIZES.MD} />,
          disabled: !selectedRows?.length,
          hide: [TRANSACTIONS_TABS.TRANSACTIONS_ATTACHMENTS].includes(
            activeTab
          ),
        },
        {
          icon: <MdOutlineAutoDelete size={ICON_SIZES.MD} />,
          onClick: () => setShowRecycleTransactionsDialog(true),
          text: t("common_menu_items.recycle"),
          kind: EButtonKind.DANGER,
          disabled: !selectedRows?.length,
          hide: [
            TRANSACTIONS_TABS.TRANSACTIONS_ATTACHMENTS,
            TRANSACTIONS_TABS.RECYCLED_TRANSACTIONS,
          ].includes(activeTab),
        },
        {
          icon: <MdDelete size={ICON_SIZES.MD} />,
          onClick: () => setShowDeleteTransactionsDialog(true),
          text: t("common_menu_items.delete"),
          kind: EButtonKind.DANGER,
          disabled: !selectedRows?.length,
          hide: [TRANSACTIONS_TABS.TRANSACTIONS_ATTACHMENTS].includes(
            activeTab
          ),
        },
        {
          icon: <MdConnectWithoutContact size={ICON_SIZES.MD} />,
          onClick: () => setShowShareTransactionsDialog(true),
          text: t("common_menu_items.send"),
          disabled: !selectedRows?.length,
          hide: [TRANSACTIONS_TABS.TRANSACTIONS_ATTACHMENTS].includes(
            activeTab
          ),
        },
      ],
      ["text"],
      ["asc"]
    );
  }, [
    selectedRows,
    activeTab,
    transactionsFilteredByYear,
    accountParameter,
    currentAccountData,
    t,
  ]);

  const actionsOptions = useMemo(() => {
    return orderBy(
      [
        {
          tooltip: t("shared.archive"),
          className: clsx(["!py-7", "!px-4"]),
          onClick: () => {
            setShowArchiveTransactionsDialog(true);
          },
          icon: <MdOutlineArchive size={ICON_SIZES.MD_LG} />,
          disabled: !selectedRows?.length,
          hide: [
            TRANSACTIONS_TABS.ARCHIVED_TRANSACTIONS,
            TRANSACTIONS_TABS.TRANSACTIONS_ATTACHMENTS,
          ].includes(activeTab),
        },
        {
          tooltip: t("shared.unarchive"),
          className: clsx(["!py-7", "!px-4"]),
          onClick: () => {
            setShowUnarchiveTransactionsDialog(true);
          },
          icon: <MdOutlineUnarchive size={ICON_SIZES.MD_LG} />,
          disabled: !selectedRows?.length,
          hide: ![TRANSACTIONS_TABS.ARCHIVED_TRANSACTIONS].includes(activeTab),
        },
        {
          icon: <MdRestoreFromTrash size={ICON_SIZES.MD_LG} />,
          className: clsx(["!py-7", "!px-4"]),
          onClick: () => setShowRestoreTransactionsDialog(true),
          tooltip: t("Restaurar"),
          kind: EButtonKind.SAVE,
          disabled: !selectedRows?.length,
          hide: ![TRANSACTIONS_TABS.RECYCLED_TRANSACTIONS].includes(activeTab),
        },
        {
          tooltip: t("Duplicar"),
          className: clsx(["!py-7", "!px-4"]),
          onClick: () => {
            setShowDuplicateTransactionDialog(true);
          },
          icon: <MdOutlineContentCopy size={ICON_SIZES.MD_LG} />,
          disabled: !selectedRows?.length,
          hide: [TRANSACTIONS_TABS.TRANSACTIONS_ATTACHMENTS].includes(
            activeTab
          ),
        },
        {
          tooltip: t("common_tooltips.export"),
          className: clsx(["!py-7", "!px-4"]),
          onClick: () => {
            setShowExportDialog(true);
          },
          icon: <MdOutlineFileDownload size={ICON_SIZES.MD_LG} />,
          disabled: !canExport,
        },

        {
          icon: <MdOutlineAutoDelete size={ICON_SIZES.MD_LG} />,
          className: clsx(["!py-7", "!px-4"]),
          onClick: () => setShowRecycleTransactionsDialog(true),
          tooltip: t("Papelera"),
          kind: EButtonKind.DANGER,
          disabled: !selectedRows?.length,
          hide: [
            TRANSACTIONS_TABS.TRANSACTIONS_ATTACHMENTS,
            TRANSACTIONS_TABS.RECYCLED_TRANSACTIONS,
          ].includes(activeTab),
        },
      ],
      ["tooltip"],
      ["asc"]
    );
  }, [
    selectedRows,
    activeTab,
    transactionsFilteredByYear,
    accountParameter,
    currentAccountData,
  ]);

  const transformData = ({
    data,
    includeCurrencySymbol,
  }: {
    data: any;
    includeCurrencySymbol: boolean;
  }) => {
    return map(data, (item) => {
      const hasDetails = Array.isArray(item.details) && item.details.length > 0;
      const mergedDescriptions = hasDetails
        ? item.details.map((d: any) => d.description).join("\n")
        : item.description;

      const currency = includeCurrencySymbol ? currencySymbol : "";

      return {
        Fecha: get(item, "date", ""),
        Documento: get(item, "documentNumber", ""),
        "Origen / Destino": get(item, "payTo", ""),
        Descripción: mergedDescriptions,
        Detallada: item?.details?.length > 0,
        Líneas: item?.details?.length,
        Categoría: get(item, "category", ""),
        Subtotal: `${currency}${get(item, "subTotal", 0) || 0}`,
        Impuestos: `${currency}${get(item, "taxes", 0) || 0}`,
        Descuento: `${currency}${get(item, "discount", 0) || 0}`,
        Total: `${currency}${get(item, "total", 0) || 0}`,
        Tipo: item.type === "EXPENSE" ? "Gasto" : "Ingreso",
        "Método de pago": get(item, "paymentMethod", ""),
        Estado: get(item, "status", ""),
        Archivos: item?.attachments?.length,
        Recurrente: get(item, "recurring.enabled", false),
        Notas: get(item, "notes", ""),
        "En la papelera": get(item, "deleted", false),
        Archivada: get(item, "archived", false),
      };
    });
  };

  const getDataToExport = ({
    exportType,
    includeCurrencySymbol,
  }: {
    exportType: Partial<EXPORT_TYPE>;
    includeCurrencySymbol: boolean;
  }) => {
    switch (activeTab) {
      case TRANSACTIONS_TABS.ACTIVE_TRANSACTIONS: {
        return transformData({
          data:
            exportType === EXPORT_TYPE.ALL
              ? transactionsFilteredByYear
              : selectedRows,
          includeCurrencySymbol,
        });
      }
      case TRANSACTIONS_TABS.ARCHIVED_TRANSACTIONS: {
        return transformData({
          data:
            exportType === EXPORT_TYPE.ALL
              ? archivedTransactions
              : selectedRows,
          includeCurrencySymbol,
        });
      }
      case TRANSACTIONS_TABS.RECYCLED_TRANSACTIONS: {
        return transformData({
          data:
            exportType === EXPORT_TYPE.ALL ? deletedTransactions : selectedRows,
          includeCurrencySymbol,
        });
      }
      default: {
        return [];
      }
    }
  };

  const exportData = ({
    includeCurrencySymbol,
    formatNumbers,
    fileName,
    fileFormat,
    exportType,
  }: any) => {
    setIsExportingData(false);

    const data = getDataToExport({ exportType, includeCurrencySymbol });

    try {
      if (fileFormat === FILE_TYPES.XLSX) {
        exportToExcel({ data, fileName });
      } else if (fileFormat === FILE_TYPES.CSV) {
        exportToCsv({ data, fileName });
      } else if (fileFormat === FILE_TYPES.JSON) {
        exportToJson({ data, fileName });
      }

      setShowExportDialog(false);
      showNotification({
        kind: "success",
        message: "Datos exportados con éxito",
      });
    } catch (e) {
      showNotification({
        kind: "error",
        message: "Ocurrió un error al exportar los datos",
      });
    } finally {
      setIsExportingData(false);
    }
  };

  const mobileMenuMarquee = useMemo(() => {
    switch (transactionsActiveTab) {
      case TRANSACTIONS_TABS.ACTIVE_TRANSACTIONS: {
        return t("transactions_page.marquee.active_transactions");
      }
      case TRANSACTIONS_TABS.ARCHIVED_TRANSACTIONS: {
        return t("transactions_page.marquee.archived_transactions");
      }
      case TRANSACTIONS_TABS.RECYCLED_TRANSACTIONS: {
        return t("transactions_page.marquee.recycled_transactions");
      }
      default: {
        return null;
      }
    }
  }, [transactionsActiveTab, t]);

  const selectedTransactionsAttachments = useMemo(() => {
    return selectedRows?.reduce?.((acc: any, curr: any) => {
      if (curr?.attachments?.length) {
        acc.push(curr?.attachments);
      }

      return acc;
    }, []);
  }, [selectedRows]);

  return (
    <>
      <TwoSidebars
        isLoading={isLoadingSomething}
        right={
          isLoadingSomething ||
          !mainFilter?.account ||
          !currentAccountData ||
          currentAccountData?.settings?.disabled ? null : isMobile ? null : (
            <ReportsSidebar
              append={
                <>
                  <SelectionTotals transactions={selectedRows} />
                  {/* <CollapsibleBlock
                    title="Acciones"
                    buttons={
                      actionsOptions?.map?.((button: any) => ({
                        ...button,
                        tooltip: button?.text,
                      })) || []
                    }
                  >
                    <div
                      className={clsx([
                        "flex",
                        "w-full",
                        "text-sm",
                        "md:text-base",
                      ])}
                    >
                      Ejecutar acciones comunes.
                    </div>
                  </CollapsibleBlock> */}

                  {/* <CollapsibleBlock title="Archivos">
                    <div
                      className={clsx([
                        "flex",
                        "w-full",
                        "text-sm",
                        "md:text-base",
                      ])}
                    >
                      {selectedRows?.length ? (
                        <AttachmentsSelector
                          onChange={() => {}}
                          onClick={() => {}}
                          savedAttachments={selectedTransactionsAttachments}
                        />
                      ) : (
                        <div>Hola</div>
                      )}
                    </div>
                  </CollapsibleBlock> */}
                </>
              }
            />
          )
        }
        mobileMenuChildren={<MarqueeRow text={mobileMenuMarquee} />}
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "gap-2",
            "md:gap-2",
            "h-full",
          ])}
        >
          <AnimatePresence>
            {mainFilter?.account ? (
              <>
                {isLoadingSomething ? (
                  <LoadingWithTextPage />
                ) : (
                  <>
                    {currentAccountData &&
                    !currentAccountData?.settings?.disabled ? (
                      <>
                        <PageTitle
                          icon={<MdOutlineSwapVert size={ICON_SIZES.MD} />}
                          title={t("transactions_page.title")}
                          subtitle={
                            isMobile && accountParameter ? (
                              <PageSubtitleAccount {...currentAccountData} />
                            ) : null
                          }
                          rightSide={
                            <>
                              <ButtonGroup
                                buttons={[
                                  {
                                    icon: <MdMoreHoriz size={ICON_SIZES.MD} />,
                                    tooltip: t("common_tooltips.more_options"),
                                    className: isMobile
                                      ? BUTTON_STYLES.HEADING
                                      : "",
                                    options: menuOptions,
                                  },
                                  {
                                    icon: isMainFilterActive ? (
                                      <MdFilterAlt
                                        className="text-red-500 dark:text-purple-500"
                                        size={ICON_SIZES.MD}
                                      />
                                    ) : (
                                      <MdFilterAlt size={ICON_SIZES.MD} />
                                    ),
                                    onClick: () => {
                                      setShowSearchModal(true);
                                    },
                                    tooltip: t("common_tooltips.filter"),
                                    // hide: !isMobile,
                                    className: isMobile
                                      ? BUTTON_STYLES.HEADING
                                      : "",
                                  },
                                  accountsGlobalMenu,
                                  generalOptionsDropdown,
                                ]}
                                gap={1}
                              />
                            </>
                          }
                          isLoading={isLoadingCurrentAccount}
                        />

                        <TodayTotalsBar />
                        <SlideDownUpAnimation
                          show={
                            !!(isMobile && collapsiblePreferences.accountOpen)
                          }
                        >
                          <CurrentAccountCard />
                        </SlideDownUpAnimation>

                        <div
                          className={clsx([
                            "flex",
                            "flex-col",
                            "gap-4",
                            "w-full",
                          ])}
                          id="focused-section"
                        >
                          <LinksList
                            links={[
                              {
                                text: t("transactions_page.navigation.active"),
                                onClick: () => {
                                  setActiveTab(
                                    TRANSACTIONS_TABS.ACTIVE_TRANSACTIONS
                                  );
                                },
                                active:
                                  activeTab ===
                                  TRANSACTIONS_TABS.ACTIVE_TRANSACTIONS,
                                icon: (
                                  <MdOutlineSwapVert size={ICON_SIZES.MD_LG} />
                                ),
                                count: transactionsFilteredByYear?.length,
                                showCount: !!transactionsFilteredByYear?.length,
                                isLoading:
                                  activeTab ===
                                    TRANSACTIONS_TABS.ACTIVE_TRANSACTIONS &&
                                  isPending,
                              },
                              {
                                text: t(
                                  "transactions_page.navigation.archived"
                                ),
                                onClick: () => {
                                  setActiveTab(
                                    TRANSACTIONS_TABS.ARCHIVED_TRANSACTIONS
                                  );
                                },
                                active:
                                  activeTab ===
                                  TRANSACTIONS_TABS.ARCHIVED_TRANSACTIONS,
                                icon: (
                                  <MdOutlineArchive size={ICON_SIZES.MD_LG} />
                                ),
                                count: archivedTransactions?.length,
                                showCount: !!archivedTransactions?.length,
                                isLoading:
                                  activeTab ===
                                    TRANSACTIONS_TABS.ARCHIVED_TRANSACTIONS &&
                                  isPending,
                              },
                              {
                                hide: false,
                                text: t(
                                  "transactions_page.navigation.recycled"
                                ),
                                onClick: () => {
                                  setActiveTab(
                                    TRANSACTIONS_TABS.RECYCLED_TRANSACTIONS
                                  );
                                },
                                active:
                                  activeTab ===
                                  TRANSACTIONS_TABS.RECYCLED_TRANSACTIONS,
                                icon: (
                                  <MdOutlineAutoDelete
                                    size={ICON_SIZES.MD_LG}
                                  />
                                ),
                                count: deletedTransactions?.length,
                                showCount: !!deletedTransactions?.length,
                                isLoading:
                                  activeTab ===
                                    TRANSACTIONS_TABS.RECYCLED_TRANSACTIONS &&
                                  isPending,
                              },
                            ]}
                          />

                          <SlideDownUpAnimation
                            show={
                              ![
                                TRANSACTIONS_TABS.ARCHIVED_TRANSACTIONS,
                                TRANSACTIONS_TABS.RECYCLED_TRANSACTIONS,
                              ].includes(activeTab)
                            }
                          >
                            <div
                              className={clsx([
                                "flex",
                                "flex-col",
                                "gap-4",
                                "w-full",
                              ])}
                            >
                              <ActiveMainFilterMessage
                                onShowSearch={() => setShowSearchModal(true)}
                              />
                              <ReportsYearSelector />
                              <IncomeExpensesPerYear />
                            </div>
                          </SlideDownUpAnimation>

                          <SlideDownUpAnimation
                            show={
                              [
                                TRANSACTIONS_TABS.ARCHIVED_TRANSACTIONS,
                                TRANSACTIONS_TABS.RECYCLED_TRANSACTIONS,
                              ].includes(activeTab) && isMainFilterActive
                            }
                          >
                            <InfoBlock
                              message="El filtro no afecta las transacciones archivadas ni en la papelera. Se muestran todas."
                              buttons={[
                                {
                                  text: isMobile
                                    ? ""
                                    : t("shared.remove_filter"),
                                  tooltip: t("shared.remove_filter"),
                                  kind: EButtonKind.DANGER,
                                  icon: <MdFilterAltOff size={ICON_SIZES.MD} />,
                                  onClick: () => {
                                    setMainFilter({
                                      dateFrom: "",
                                      dateTo: "",
                                      type: "",
                                      payTo: [],
                                      status: [],
                                      paymentMethod: [],
                                      tags: [],
                                      category: [],
                                    });
                                  },
                                },
                              ]}
                            />
                          </SlideDownUpAnimation>

                          <motion.div
                            className={clsx([
                              "w-full",
                              "flex",
                              "flex-col",
                              "gap-6",
                            ])}
                            key={activeTab}
                            onAnimationComplete={() => setCanScroll(true)}
                            {...(isMobile
                              ? {
                                  initial: {
                                    opacity: 0.99,
                                  },
                                  animate: { opacity: 1 },
                                  transition: { duration: 0.1 },
                                }
                              : {
                                  initial: { opacity: 0 },
                                  animate: { opacity: 1 },
                                  exit: { opacity: 0 },
                                  transition: { duration: 2, ease: "easeOut" },
                                })}
                          >
                            {/* {isPending ? (
                              <LoadingWithTextPage />
                            ) : ( */}
                            <>
                              {!isMobile && (
                                <MarqueeRow text={mobileMenuMarquee} />
                              )}

                              {activeTab ===
                                TRANSACTIONS_TABS.ACTIVE_TRANSACTIONS && (
                                <TransactionsTable
                                  isLoading={isFilteringTransactions}
                                  transactions={transactionsFilteredByYear}
                                  onSelectionChange={(selection) =>
                                    setSelectedRows(selection)
                                  }
                                  settingsKey={
                                    ETransactionsTableKeys.ACTIVE_TRANSACTIONS
                                  }
                                  noDataButtons={[
                                    {
                                      onClick: () =>
                                        navigate(
                                          `/transaction/${
                                            mainFilter.account || ""
                                          }`
                                        ),
                                      text: "Nueva transacción",
                                      icon: <MdAdd size={ICON_SIZES.MD} />,
                                      kind: EButtonKind.SAVE,
                                    },
                                  ]}
                                />
                              )}
                              {activeTab ===
                                TRANSACTIONS_TABS.ARCHIVED_TRANSACTIONS && (
                                <TransactionsTable
                                  isLoading={isFilteringTransactions}
                                  transactions={archivedTransactions}
                                  onSelectionChange={(selection) =>
                                    setSelectedRows(selection)
                                  }
                                  settingsKey={
                                    ETransactionsTableKeys.ARCHIVED_TRANSACTIONS
                                  }
                                />
                              )}
                              {activeTab ===
                                TRANSACTIONS_TABS.RECYCLED_TRANSACTIONS && (
                                <TransactionsTable
                                  isLoading={isFilteringTransactions}
                                  transactions={deletedTransactions}
                                  onSelectionChange={(selection) =>
                                    setSelectedRows(selection)
                                  }
                                  settingsKey={
                                    ETransactionsTableKeys.RECYCLED_TRANSACTIONS
                                  }
                                />
                              )}
                            </>
                            {/* )} */}

                            {isMobile && (
                              <SelectionTotals transactions={selectedRows} />
                            )}
                          </motion.div>
                        </div>

                        <ScrollToSection
                          targetId="focused-section"
                          enabled={canScroll}
                        />
                      </>
                    ) : (
                      <AccountNotFound
                        exists={!!currentAccountData}
                        disabled={!!currentAccountData?.settings?.disabled}
                        linkTo="transactions"
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <AccountNotFound linkTo="transactions" />
            )}
          </AnimatePresence>
        </div>
      </TwoSidebars>

      {showExportDialog && (
        <ExportTransactionsModal
          onClose={() => setShowExportDialog(false)}
          onConfirm={async (data: any) => {
            exportData(data);
          }}
          isLoading={isExportingData}
          selectedRows={selectedRows?.length}
          totalRows={
            getDataToExport({
              exportType: EXPORT_TYPE.ALL,
              includeCurrencySymbol: false,
            })?.length
          }
        />
      )}

      {selectedRows?.length > 0 && (
        <>
          {showDuplicateTransactionDialog && (
            <DuplicateTransactionsDialog
              onClose={() => setShowDuplicateTransactionDialog(false)}
              isLoading={isDuplicatingTransactions}
              onConfirm={async (values: any) => {
                await duplicateSelection(values);
              }}
              count={selectedRows?.length}
            />
          )}
          {showDeleteTransactionsDialog && (
            <ConfirmDialog
              description={t("dialogs.delete_transactions.delete_question", {
                count: selectedRows?.length,
              })}
              note={t("dialogs.delete_transactions.delete_question_note", {
                count: selectedRows?.length,
              })}
              onClose={() => setShowDeleteTransactionsDialog(false)}
              doubleConfirmRequired
              onConfirm={async () => await deleteSelection()}
              title={t("dialogs.delete_transactions.title")}
              confirmKind={EButtonKind.DANGER}
              isLoading={isDeletingTransactions}
            />
          )}
          {showRecycleTransactionsDialog && (
            <ConfirmDialog
              description={t("dialogs.recycle_transactions.recycle_question", {
                count: selectedRows?.length,
              })}
              onClose={() => setShowRecycleTransactionsDialog(false)}
              onConfirm={async () => await recycleSelection()}
              title={t("dialogs.recycle_transactions.title")}
              confirmKind={EButtonKind.DANGER}
              isLoading={isRecyclingTransactions}
            />
          )}
          {showRestoreTransactionsDialog && (
            <ConfirmDialog
              description={t("dialogs.restore_transactions.restore_question", {
                count: selectedRows?.length,
              })}
              onClose={() => setShowRestoreTransactionsDialog(false)}
              onConfirm={async () => await restoreSelection()}
              title={t("dialogs.restore_transactions.title")}
              confirmKind={EButtonKind.SAVE}
              isLoading={isRestoringTransactions}
            />
          )}
          {showArchiveTransactionsDialog && (
            <ConfirmDialog
              description={t("dialogs.archive_transactions.archive_question", {
                count: selectedRows?.length,
              })}
              onClose={() => setShowArchiveTransactionsDialog(false)}
              onConfirm={async () => await archiveSelection()}
              title={t("dialogs.archive_transactions.title")}
              isLoading={isArchivingTransactions}
            />
          )}
          {showUnarchiveTransactionsDialog && (
            <ConfirmDialog
              description={t(
                "dialogs.unarchive_transactions.unarchive_question",
                {
                  count: selectedRows?.length,
                }
              )}
              onClose={() => setShowUnarchiveTransactionsDialog(false)}
              onConfirm={async () => await unarchiveSelection()}
              title={t("dialogs.unarchive_transactions.title")}
              isLoading={isUnarchivingTransactions}
            />
          )}
          {showMoveTransactionsDialog && (
            <SelectDialog
              description={t("dialogs.move_transactions.move_question", {
                count: selectedRows?.length,
              })}
              doubleConfirmRequired
              note={
                allUserAccounts?.length === 1
                  ? t("dialogs.move_transactions.move_need_another_account")
                  : t("dialogs.move_transactions.move_question_note", {
                      count: selectedRows?.length,
                    })
              }
              onClose={() => {
                setShowMoveTransactionsDialog(false);
              }}
              onConfirm={async (to: string) => {
                await moveSelection({ newAccount: to });
              }}
              isLoading={isMovingTransactions}
              title={t("dialogs.move_transactions.title")}
              label="Cuenta destino"
              confirmKind={EButtonKind.SAVE}
              options={
                transformToSelectOptions({
                  data: allUserAccounts?.filter?.(
                    (account: any) => account?.account !== mainFilter?.account
                  ),
                  labelField: "name",
                  valueField: "account",
                }) as Array<ICustomSelectOption>
              }
            />
          )}

          {showShareTransactionsDialog && (
            <ShareTransactionModal
              onClose={() => setShowShareTransactionsDialog(false)}
              isLoading={isSharingTransactions}
              onSend={async (shareWith: any) => {
                setIsSharingTransactions(true);

                try {
                  await shareTransactions({
                    shareWith,
                    transactions: selectedRows?.map?.(
                      (transaction: any) => transaction?.invoice
                    ),
                    currencyCode,
                  });

                  if (selectedRows?.length === 1) {
                    showNotification({
                      message: "Transacción enviada correctamente",
                      kind: "success",
                    });
                  } else {
                    showNotification({
                      message: "Transacciones enviadas correctamente",
                      kind: "success",
                    });
                  }
                  setShowShareTransactionsDialog(false);
                } catch (_e) {
                  if (selectedRows?.length === 1) {
                    showNotification({
                      message: "Error al enviar la transacción",
                      kind: "error",
                    });
                  } else {
                    showNotification({
                      message: "Error al enviar las transacciones",
                      kind: "error",
                    });
                  }
                } finally {
                  setIsSharingTransactions(false);
                }
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default Transactions;
